import { useEffect, useMemo } from "react";
import { getBasicQueryVars, replaceStringVar } from "@/core/utils";
import { HIGHLIGHT_COLORS } from "@/core/constants";
import { InducteesByClassQuery } from "@/queries/Inductees";
import { FaustTemplate, useFaustQuery } from "@faustwp/core";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { SEO, Hero, ListingInductees, CarouselClasses, FloatSpotlight, Title, Feature } from "@components";
import { SingleExhibitsInteractives } from "@/components/FeatureExhibitsInteractives/SingleExhibitsInteractives";
import MediaFlexibleContentMap from "@/components/ComponentMaps/Ceremony/MediaFlexibleContentMap";
import { CeremonyLandingQuery, AdjacentClassesQuery, ClassExhibitQuery } from "@/queries/Ceremony";
import AdditionalFlexibleContentMap from "@/components/ComponentMaps/Ceremony/AdditionalFlexibleContentMap";
import { GetAdjacentClassesQuery, GetCeremonyLandingQuery, GetClassExhibitQuery, GetInducteesByClassQuery } from "@graphqlTypes";
import { getExhibitData } from "@/components/FeatureExhibitsInteractives/utils";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

const CeremonyLanding: FaustTemplate<any> = ({ loading }: FaustPageProps<any>) => {
  const ceremonyData = useFaustQuery<GetCeremonyLandingQuery>(CeremonyLandingQuery);
  const inducteeData = useFaustQuery<GetInducteesByClassQuery>(InducteesByClassQuery);
  const exhibitData = useFaustQuery<GetClassExhibitQuery>(ClassExhibitQuery);
  const adjacentClassesData = useFaustQuery<GetAdjacentClassesQuery>(AdjacentClassesQuery);
  const { rhCeremony } = ceremonyData || {};
  const { labels, siteSettings } = usePageWrapperData();
  const { ceremonyFeature, ceremonyFloatSpotlight, ceremonyHero } = siteSettings || {};
  const { setHighlightColor } = usePageHighlight();
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const ogTitle = labels?.ceremonyLandingOgTitle ? replaceStringVar(labels.ceremonyLandingOgTitle, { year: rhCeremony?.slug }) : '';
  const {
    pageData: { ogDescription, featuredImage, pageCategory } = {}
  } = rhCeremony || {};
  const {
    featuredCeremony,
    season,
    classYear,
    featuredHero,
    useDefaultContent,
    mediaFlexibleContent,
    additionalFlexibleContent,
  } = rhCeremony?.ceremonyLanding || {};
  const inducteeClassYear = classYear?.[0]?.slug || 'NO YEAR';
  const [currentSeason] = season || [''];
  const isInducteeSeason = currentSeason === 'inductee-season';
  const inductees: GetInducteesByClassQuery["relatedInductees"] = inducteeData?.relatedInductees || { nodes: [] };
  const adjacentInductees = adjacentClassesData?.adjacentInducteeClasses || [];
  const [category] = pageCategory || [];
  const defaultHeroHeadline = useMemo(() => {
    return replaceStringVar(ceremonyHero?.headline || '', { year: inducteeClassYear });
  }, [inducteeClassYear]);

  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLORS.YELLOW);
  }, [category]);
  useTrackRouteChange(category?.slug);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  if (loading || !ceremonyData || !ceremonyData.rhCeremony) return;
  return (
    <>
      <SEO title={ogTitle} description={ogDescription} image={featuredImage} />
      { featuredCeremony ? (
          <Hero variant={['primary']} slides={[{ ...featuredHero, eyebrow: ceremonyHero?.eyebrow }]} />
        ) : (
          <Hero variant={['secondary']} slides={[{ image: ceremonyHero?.image, eyebrow: ceremonyHero?.eyebrow, contentType: ['induction'], headline: defaultHeroHeadline }]} />
        )
      }

      { !isInducteeSeason && mediaFlexibleContent && (
          <MediaFlexibleContentMap content={mediaFlexibleContent} />
      )}
      { exhibitData?.classOfExhibit && isInducteeSeason && (
          <SingleExhibitsInteractives
            exhibit={getExhibitData(exhibitData.classOfExhibit)}
            headline={labels?.featuredExhibitHeadline}
            largeTitle={true}
            pillLabel={labels?.ceremonyNewExhibit}
          />
        )
      }
      <ListingInductees showHeader={featuredCeremony} inductees={inductees} classYear={inducteeClassYear} />
      { useDefaultContent && (
        <>
          <FloatSpotlight {...ceremonyFloatSpotlight} />
          <Title headline={ceremonyFeature?.headline || ''} />
          <Feature items={ceremonyFeature?.items || []} float={false} />
        </>
      )}
      {  additionalFlexibleContent && !useDefaultContent && (
          <AdditionalFlexibleContentMap content={additionalFlexibleContent} />
      )}
      {
        adjacentInductees.length && (
          <CarouselClasses inductees={adjacentInductees} />
        )
      }
    </>
  );
}

CeremonyLanding.queries = [
  {
    query: CeremonyLandingQuery,
    variables: getBasicQueryVars,
  },
  {
    query: InducteesByClassQuery,
    variables: ({ databaseId }) => ({ databaseId })
  },
  {
    query: ClassExhibitQuery,
    variables: ({ databaseId }) => ({ databaseId })
  },
  {
    query: AdjacentClassesQuery,
    variables: ({ databaseId }) => ({ databaseId })
  }
]

export default CeremonyLanding;
